import React, { useState, useEffect, useRef } from 'react';
import { Groq } from 'groq-sdk';
import { v4 as uuidv4 } from 'uuid';
import OpenAI from 'openai';
import { Conversation, Message, Model, StreamingStatus, MessageContent, ThinkingState, UserPlan } from './types';
import { getConversations, saveConversations, getCurrentConversationId, saveCurrentConversationId } from './utils/storage';
import { useNotifications } from './contexts/NotificationContext';
import BackgroundEffects from './components/BackgroundEffects';
import ConversationList from './components/ConversationList';
import ChatMessage from './components/ChatMessage';
import ChatInput from './components/ChatInput';
import ModelSelector from './components/ModelSelector';
import { Provider } from './components/Providers';
import { ExtendedModel } from './components/Models';
import { providers, getProviderById } from './config/providers';
import { customModels, getCustomModelById } from './config/customModels';
import { models, getModelById, modelHasCapability } from './models';
import { UploadedFile } from './components/ChatInput';
import { extractThinking } from './utils/text';
import AuthScreen from './components/AuthScreen';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Settings from './components/Settings';
import ProfileButton from './components/ProfileButton';
import { hasReachedLimit, incrementUsage, getRemainingMessages, getRateLimitResetTime, getMessageLimit, getUserPlan, getRemainingProModelMessages, getProModelsResetInfo } from './utils/rateLimit';
import { streamAnthropicMessage } from './anthropic';
import { streamOpenAIMessage } from './openai';
import { sendXaiMessage } from './xai';
import { sendGeminiMessage } from './gemini';
import { NotificationProvider } from './contexts/NotificationContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';

const getCurrentDate = () => {
    const date = new Date();
    return date.toISOString();
};

const groq = new Groq({
    apiKey: process.env.REACT_APP_GROQ_API_KEY,
    dangerouslyAllowBrowser: true
});

const convertCustomModelsToModels = (customModels: ExtendedModel[]): Model[] => {
    return customModels.map(model => ({
        id: model.id,
        name: model.name,
        description: model.description,
        icon: model.icon,
        category: model.category,
        capabilities: model.capabilities,
        isNew: model.isNew,
        isPremium: false,
    }));
};

const allModels: Model[] = [
    ...models,
    ...convertCustomModelsToModels(customModels)
];

const isStreaming = (status: StreamingStatus) => status === 'streaming';
const isIdle = (status: StreamingStatus) => status === 'idle';
const isError = (status: StreamingStatus) => status === 'error';
const isSearching = (status: StreamingStatus) => status === 'searching';

function MainApp() {
    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [currentConversationId, setCurrentConversationId] = useState<string>('');
    const [streamingStatus, setStreamingStatus] = useState<StreamingStatus>('idle');
    const [showSidebar, setShowSidebar] = useState(true);
    const [activeModel, setActiveModel] = useState(models[0].id);

    const [searchEnabled, setSearchEnabled] = useState(false);

    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [username, setUsername] = useState(() => {
        return localStorage.getItem('versetaille_username') || '';
    });
    const [modelBehavior, setModelBehavior] = useState(() => {
        return localStorage.getItem('versetaille_behavior') || 'standard';
    });

    const { showNotification } = useNotifications();
    const { currentUser } = useAuth();
    const { theme, toggleTheme, effectiveTheme } = useTheme();

    const [userPlan, setUserPlan] = useState<UserPlan>('free');
    const [isPlanLoading, setIsPlanLoading] = useState(true);

    const [remainingMessages, setRemainingMessages] = useState<number>(10);
    const [rateLimitReset, setRateLimitReset] = useState(() => getRateLimitResetTime());
    const [isRateLimitChecking, setIsRateLimitChecking] = useState(false);

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const streamedTextRef = useRef<string>('');

    const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
    const [editingMessageContent, setEditingMessageContent] = useState<string>('');

    const [regenerateModel, setRegenerateModel] = useState<string | null>(null);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [regenerationMessageId, setRegenerationMessageId] = useState<string | null>(null);

    const currentConversation = conversations.find(c => c.id === currentConversationId);

    useEffect(() => {
        const storedConversations = getConversations();
        if (storedConversations.length > 0) {
            setConversations(storedConversations);

            const storedCurrentId = getCurrentConversationId();
            const validId = storedCurrentId && storedConversations.some(c => c.id === storedCurrentId)
                ? storedCurrentId
                : storedConversations[0].id;

            setCurrentConversationId(validId);
        } else {
            handleNewConversation();
        }
    }, []);

    useEffect(() => {
        if (conversations.length > 0) {
            saveConversations(conversations);
            saveCurrentConversationId(currentConversationId);
        }
    }, [conversations, currentConversationId]);

    useEffect(() => {
        scrollToBottom();
    }, [currentConversation?.messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleNewConversation = () => {
        const newId = uuidv4();
        const newConversation: Conversation = {
            id: newId,
            title: 'New Conversation',
            messages: [],
            createdAt: Date.now(),
            updatedAt: Date.now(),
            model: activeModel
        };

        setConversations(prev => [newConversation, ...prev]);
        setCurrentConversationId(newId);
        showNotification('New conversation created', 'success');
    };

    const handleDeleteConversation = (id: string) => {
        setConversations(prev => prev.filter(c => c.id !== id));

        if (id === currentConversationId && conversations.length > 1) {
            const remainingConvs = conversations.filter(c => c.id !== id);
            setCurrentConversationId(remainingConvs[0].id);
        } else if (conversations.length <= 1) {
            handleNewConversation();
        }

        showNotification('Conversation deleted', 'info');
    };

    const updateConversationTitle = (id: string, content: string) => {
        setConversations(prev =>
            prev.map(conv => {
                if (conv.id === id) {
                    const newTitle = content.split(' ').slice(0, 5).join(' ') + '...';
                    return { ...conv, title: newTitle };
                }
                return conv;
            })
        );
    };

    useEffect(() => {
        console.log('Streaming status changed:', streamingStatus);

        if (streamingStatus === 'done' || streamingStatus === 'error') {
            const timer = setTimeout(() => {
                console.log('Resetting streaming status to idle');
                setStreamingStatus('idle');
            }, 500);

            return () => clearTimeout(timer);
        }
    }, [streamingStatus]);

    useEffect(() => {
        if (username) {
            localStorage.setItem('versetaille_username', username);
        }
    }, [username]);

    useEffect(() => {
        if (modelBehavior) {
            localStorage.setItem('versetaille_behavior', modelBehavior);
        }
    }, [modelBehavior]);

    useEffect(() => {
        const updateResetTime = () => {
            setRateLimitReset(getRateLimitResetTime());
        };

        const intervalId = setInterval(updateResetTime, 60000);
        return () => clearInterval(intervalId);
    }, []);

    const refreshUserData = async () => {
        if (!currentUser) {
            setUserPlan('free');
            setIsPlanLoading(false);
            setRemainingMessages(getMessageLimit('free'));
            return;
        }

        try {
            const plan = await getUserPlan(currentUser.uid);
            setUserPlan(plan);

            if (plan === 'pro') {
                const remaining = await getRemainingProModelMessages(currentUser.uid);
                setRemainingMessages(remaining);
            } else {
                const remaining = await getRemainingMessages(currentUser.uid);
                setRemainingMessages(remaining);
            }

            setIsPlanLoading(false);
        } catch (error) {
            console.error('Error refreshing user data:', error);
            setUserPlan('free');
            setRemainingMessages(getMessageLimit('free'));
            setIsPlanLoading(false);
        }
    };

    useEffect(() => {
        refreshUserData();
        setRateLimitReset(getRateLimitResetTime());
    }, [currentUser?.uid]);

    useEffect(() => {
        if (!currentUser?.uid) return;

        const updateRemaining = async () => {
            try {
                const remaining = await getRemainingMessages(currentUser.uid);
                setRemainingMessages(remaining);
            } catch (error) {
                console.error('Error refreshing remaining messages:', error);
            }
        };

        const intervalId = setInterval(updateRemaining, 60000);
        return () => clearInterval(intervalId);

    }, [currentUser?.uid]);

    const performSearch = async (query: string): Promise<string> => {
        showNotification('🔍 Searching the web for information...', 'info');
        const agentResponse = await groq.chat.completions.create({
            model: 'compound-beta',
            messages: [
                { role: 'system', content: 'You are a Versetaille search assistant that returns raw search results from the internet.' },
                { role: 'user', content: query }
            ],
            temperature: 0.0,
            max_tokens: 2048
        });
        return agentResponse.choices[0]?.message?.content || '';
    };

    const handleSendMessage = async (content: string, files?: UploadedFile[], searchMode: boolean = false) => {
        if (!currentConversation) return;

        try {
            setIsRateLimitChecking(true);
            const modelId = currentConversation.model || activeModel;
            const isLimitReached = await hasReachedLimit(currentUser?.uid || 'anonymous', modelId);

            if (isLimitReached) {
                const model = getModelById(modelId);
                const modelPlanRequired = model?.planRequired || 'free';

                if (userPlan === 'free' && (modelPlanRequired === 'plus' || modelPlanRequired === 'pro')) {
                    showNotification(`You need a ${modelPlanRequired} plan to use this model.`, 'error');
                    setIsRateLimitChecking(false);
                    return;
                } else if (userPlan === 'plus' && modelPlanRequired === 'pro') {
                    showNotification('You need a Pro plan to use this model.', 'error');
                    setIsRateLimitChecking(false);
                    return;
                } else if (userPlan === 'pro' && modelPlanRequired === 'pro') {
                    const { formattedDate, daysUntil } = getProModelsResetInfo();
                    showNotification(`You've reached your 200 Pro model messages limit for this month. Resets on ${formattedDate} (${daysUntil} days).`, 'error');
                    setIsRateLimitChecking(false);
                    return;
                } else {
                    const { formattedTime } = getRateLimitResetTime();
                    showNotification(`You've reached your daily message limit. Resets at ${formattedTime}.`, 'error');
                    setIsRateLimitChecking(false);
                    return;
                }
            }
            setIsRateLimitChecking(false);

            if (!currentUser?.uid) {
                showNotification('You need to be logged in to send messages', 'error');
                return;
            }

            if (!currentConversationId || streamingStatus === 'streaming') {
                console.log('Cannot send message: streaming or no conversation');
                return;
            }

            if (streamingStatus === 'done' || streamingStatus === 'error') {
                console.log('Resetting streaming status before sending message');
                setStreamingStatus('idle');
            }

            // Check if we're editing a message
            if (editingMessageId) {
                // Find the index of the message we're editing
                const messageIndex = currentConversation.messages.findIndex(
                    message => message.id === editingMessageId
                );

                if (messageIndex >= 0) {
                    // Remove this message and all subsequent messages
                    setConversations(prev =>
                        prev.map(conv => {
                            if (conv.id === currentConversationId) {
                                return {
                                    ...conv,
                                    messages: conv.messages.slice(0, messageIndex)
                                };
                            }
                            return conv;
                        })
                    );

                    // Reset editing state
                    setEditingMessageId(null);
                    setEditingMessageContent('');

                    // Continue with normal message sending flow below
                }
            }

            let searchResults = '';
            const originalQuery = content;
            let messageContent: string | MessageContent[] = content;

            if (files && files.length > 0) {
                messageContent = [];

                if (content.trim()) {
                    messageContent.push({
                        type: 'text',
                        text: content
                    });
                }

                for (const file of files) {
                    if (file.type.startsWith('image/')) {
                        messageContent.push({
                            type: 'image_url',
                            image_url: {
                                url: file.content as string
                            }
                        });
                    } else {
                        let fileType = file.type;

                        if (file.name.toLowerCase().endsWith('.json')) {
                            fileType = 'JSON File';
                        } else if (file.type === 'application/octet-stream') {
                            fileType = 'Binary File';
                        } else if (file.type.startsWith('text/')) {
                            fileType = 'Text File';
                        }

                        if (file.name.toLowerCase() === 'package.json') {
                            fileType = 'Node.js Project Configuration';
                        }

                        const fileEmbed = `[${file.name} - ${fileType}]`;

                        messageContent.push({
                            type: 'text',
                            text: fileEmbed
                        });

                        if (file.isText && typeof file.content === 'string') {
                            const fileContent = file.content;

                            messageContent.push({
                                type: 'file_content',
                                file_id: file.id,
                                name: file.name,
                                content: fileContent
                            });
                        }
                    }
                }
            }

            const selectedConversation = conversations.find(c => c.id === currentConversationId);
            const selectedModel = selectedConversation?.model || activeModel;
            const hasReasoningCapability = modelHasCapability(selectedModel, 'reasoning');

            const userMessage: Message = {
                id: uuidv4(),
                role: 'user',
                content: messageContent,
                timestamp: Date.now(),
            };

            const assistantMessage: Message = {
                id: uuidv4(),
                role: 'assistant',
                content: '',
                timestamp: Date.now(),
                thinking: '',
                thinkingStartTime: Date.now(),
                isThinking: !searchMode && hasReasoningCapability,
                isSearching: searchMode,
                searchStartTime: searchMode ? Date.now() : undefined,
            };

            setConversations(prev =>
                prev.map(conv => {
                    if (conv.id === currentConversationId) {
                        const updatedMessages = [...conv.messages, userMessage, assistantMessage];
                        return {
                            ...conv,
                            messages: updatedMessages,
                            updatedAt: Date.now()
                        };
                    }
                    return conv;
                })
            );

            if (currentConversation?.messages.length === 0) {
                updateConversationTitle(currentConversationId, content);
            }

            if (searchMode) {
                setStreamingStatus('searching');
                showNotification('🔍 Searching the web for information...', 'info');

                const rawResults = await performSearch(originalQuery);
                searchResults = rawResults;

                setStreamingStatus('streaming');
            } else {
                setStreamingStatus('streaming');
            }

            streamedTextRef.current = '';

            try {
                const modelName = allModels.find(m => m.id === selectedModel)?.name || selectedModel;

                const modelDetails = getModelById(selectedModel);
                const modelProvider = modelDetails?.provider || 'Groq';

                const customModel = getCustomModelById(selectedModel);

                let provider = undefined;
                if (customModel) {
                    provider = getProviderById(customModel.providerId);
                }

                let apiMessages = selectedConversation?.messages.slice(0, -1).map(msg => {
                    const apiMessage: any = {
                        role: msg.role
                    };

                    if (typeof msg.content === 'string') {
                        apiMessage.content = msg.content;
                    } else if (Array.isArray(msg.content)) {
                        apiMessage.content = msg.content.map(item => {
                            if (item.type === 'text' && item.text) {
                                return { type: 'text', text: item.text };
                            } else if (item.type === 'image_url' && item.image_url?.url) {
                                return {
                                    type: 'image_url',
                                    image_url: { url: item.image_url.url }
                                };
                            }
                            return null;
                        }).filter(Boolean);
                    }

                    return apiMessage;
                }) || [];

                let finalApiContent: string | Array<{ type: string; text?: string; image_url?: { url: string } }>;

                if (searchMode) {
                    finalApiContent = `Based on the following search results:\n\n<search_results>\n${searchResults}\n</search_results>\n\nPlease provide a comprehensive answer to the original query: "${originalQuery}". Summarize the key information into a neat, organized list or paragraphs, and include relevant source URLs where possible for verification.`;
                } else {
                    if (files && files.length > 0 && typeof messageContent === 'object') {
                        let combinedContent = '';
                        const textItems = messageContent.filter(item => item.type === 'text' && !item.text.match(/^\[(.*?) - (.*?)\]$/));
                        if (textItems.length > 0) {
                            combinedContent = textItems.map(item => item.type === 'text' ? item.text : '').join('\n\n');
                        }
                        const fileEmbeds = messageContent.filter(item => item.type === 'text' && item.text.match(/^\[(.*?) - (.*?)\]$/));
                        const fileContents = messageContent.filter(item => item.type === 'file_content');
                        fileEmbeds.forEach(embed => {
                            if (embed.type === 'text') {
                                const match = embed.text.match(/^\[(.*?) - (.*?)\]$/);
                                if (match) {
                                    const [_, fileName, fileType] = match;
                                    const fileContent = fileContents.find(item => item.type === 'file_content' && item.name === fileName);
                                    if (fileContent && fileContent.type === 'file_content') {
                                        combinedContent += combinedContent ? '\n\n' : '';
                                        combinedContent += `File: ${fileName} (${fileType})\n`;
                                        if (fileName.toLowerCase().endsWith('.json')) {
                                            combinedContent += '```json\n' + fileContent.content + '\n```';
                                        } else {
                                            combinedContent += '```\n' + fileContent.content + '\n```';
                                        }
                                    }
                                }
                            }
                        });
                        finalApiContent = combinedContent;
                        const imageItems = messageContent.filter(item => item.type === 'image_url');
                        if (imageItems.length > 0) {
                            finalApiContent = [
                                { type: 'text', text: combinedContent }
                            ] as Array<{ type: string; text?: string; image_url?: { url: string } }>;
                            imageItems.forEach(item => {
                                if (item.type === 'image_url' && item.image_url?.url) {
                                    (finalApiContent as Array<any>).push({
                                        type: 'image_url',
                                        image_url: { url: item.image_url.url }
                                    });
                                }
                            });
                        }
                    } else {
                        finalApiContent = originalQuery;
                    }
                }

                apiMessages.push({ role: 'user', content: finalApiContent });

                let systemPrompt = "";

                switch (modelBehavior) {
                    case 'creative':
                        systemPrompt = `You are Versetaille, a creative and imaginative AI assistant. Focus on providing original and artistic responses. ${username ? `Address the user as ${username}.` : ''}`;
                        break;
                    case 'precise':
                        systemPrompt = `You are Versetaille, a precise and factual AI assistant. Focus on accuracy and clarity in your responses. ${username ? `Address the user as ${username}.` : ''}`;
                        break;
                    case 'friendly':
                        systemPrompt = `You are Versetaille, a friendly and conversational AI assistant. Use a warm, approachable tone. ${username ? `Address the user as ${username}.` : ''}`;
                        break;
                    case 'professional':
                        systemPrompt = `You are Versetaille, a professional AI assistant. Maintain a formal, business-like tone. ${username ? `Address the user as ${username}.` : ''}`;
                        break;
                    default:
                        systemPrompt = `You are Versetaille, a helpful AI assistant. ${username ? `Address the user as ${username}.` : ''}`;
                        break;
                }

                systemPrompt += ``;

                systemPrompt += ` I'll adapt my responses to your specific needs. If you need LaTeX for math problems, formatting with markdown, or other specific formats, I'll use them appropriately based on your request.`;
                systemPrompt += ` I will not assume formatting needs unless you explicitly request them.`;
                systemPrompt += ` I'll avoid generic greetings and instead focus on directly addressing your questions and needs.`;
                systemPrompt += ` If I don't know an answer, I will clearly say so rather than providing incorrect information.`;
                systemPrompt += ` Today's date is${getCurrentDate()}`;

                if (systemPrompt || (customModel?.systemPrompt)) {
                    const finalSystemPrompt = customModel?.systemPrompt || systemPrompt;
                    apiMessages.unshift({
                        role: 'system',
                        content: finalSystemPrompt
                    });
                }

                streamedTextRef.current = '';

                showNotification(`Processing with ${modelName}...`, 'info');

                try {
                    if (modelProvider === 'OpenAI') {
                        const openaiMessages = apiMessages.map(msg => ({
                            role: msg.role,
                            content: typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content)
                        }));

                        const stream = await streamOpenAIMessage(
                            selectedModel,
                            openaiMessages,
                            customModel?.maxTokens || 1024
                        );

                        for await (const chunk of stream) {
                            const content = chunk.choices?.[0]?.delta?.content || '';

                            if (content) {
                                streamedTextRef.current += content;

                                const hasReasoning = modelHasCapability(selectedModel, 'reasoning');

                                if (hasReasoning) {
                                    const { thinking, content: cleanContent } = extractThinking(streamedTextRef.current);
                                    const thinkingIsFinished = thinking && cleanContent.trim().length > 0;
                                    setConversations(prev =>
                                        prev.map(conv => {
                                            if (conv.id === currentConversationId) {
                                                const updatedMessages = conv.messages.map(msg => {
                                                    if (msg.id === assistantMessage.id) {
                                                        return {
                                                            ...msg,
                                                            content: cleanContent,
                                                            thinking: thinking || msg.thinking,
                                                            thinkingEndTime: thinkingIsFinished ? Date.now() : msg.thinkingEndTime,
                                                            isThinking: !thinkingIsFinished,
                                                            isSearching: false
                                                        };
                                                    }
                                                    return msg;
                                                });
                                                return { ...conv, messages: updatedMessages, updatedAt: Date.now() };
                                            }
                                            return conv;
                                        })
                                    );
                                } else {
                                    setConversations(prev =>
                                        prev.map(conv => {
                                            if (conv.id === currentConversationId) {
                                                const updatedMessages = conv.messages.map(msg => {
                                                    if (msg.id === assistantMessage.id) {
                                                        return {
                                                            ...msg,
                                                            content: streamedTextRef.current,
                                                            isSearching: false
                                                        };
                                                    }
                                                    return msg;
                                                });
                                                return { ...conv, messages: updatedMessages, updatedAt: Date.now() };
                                            }
                                            return conv;
                                        })
                                    );
                                }
                            }
                        }
                    } else if (modelProvider === 'Anthropic') {
                        const anthropicMessages = apiMessages.map(msg => ({
                            role: msg.role,
                            content: typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content)
                        }));

                        const stream = await streamAnthropicMessage(
                            selectedModel,
                            anthropicMessages,
                            customModel?.maxTokens || 1024
                        );

                        for await (const event of stream) {
                            if (event.type === 'content_block_delta' && event.delta.type === 'text_delta') {
                                const textChunk = event.delta.text;
                                if (textChunk) {
                                    streamedTextRef.current += textChunk;

                                    const hasReasoning = modelHasCapability(selectedModel, 'reasoning');

                                    if (hasReasoning) {
                                        const { thinking, content: cleanContent } = extractThinking(streamedTextRef.current);
                                        const thinkingIsFinished = thinking && cleanContent.trim().length > 0;
                                        setConversations(prev =>
                                            prev.map(conv => {
                                                if (conv.id === currentConversationId) {
                                                    const updatedMessages = conv.messages.map(msg => {
                                                        if (msg.id === assistantMessage.id) {
                                                            return {
                                                                ...msg,
                                                                content: cleanContent,
                                                                thinking: thinking || msg.thinking,
                                                                thinkingEndTime: thinkingIsFinished ? Date.now() : msg.thinkingEndTime,
                                                                isThinking: !thinkingIsFinished,
                                                                isSearching: false
                                                            };
                                                        }
                                                        return msg;
                                                    });
                                                    return { ...conv, messages: updatedMessages, updatedAt: Date.now() };
                                                }
                                                return conv;
                                            })
                                        );
                                    } else {
                                        setConversations(prev =>
                                            prev.map(conv => {
                                                if (conv.id === currentConversationId) {
                                                    const updatedMessages = conv.messages.map(msg => {
                                                        if (msg.id === assistantMessage.id) {
                                                            return {
                                                                ...msg,
                                                                content: streamedTextRef.current
                                                            };
                                                        }
                                                        return msg;
                                                    });
                                                    return { ...conv, messages: updatedMessages, updatedAt: Date.now() };
                                                }
                                                return conv;
                                            })
                                        );
                                    }
                                }
                            } else if (event.type === 'message_stop') {
                                console.log('Anthropic stream stopped.');
                            }
                        }
                    } else if (modelProvider === 'Xai') {
                        const xaiMessages = apiMessages.map(msg => ({
                            role: msg.role,
                            content: typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content)
                        }));

                        try {
                            // Get proper streaming response for Xai models
                            const hasReasoning = modelHasCapability(selectedModel, 'reasoning');

                            // Call sendXaiMessage with streaming enabled (returns a stream)
                            const streamResult = await sendXaiMessage(
                                selectedModel,
                                xaiMessages,
                                customModel?.maxTokens || 2048,
                                true // Enable streaming
                            );

                            // Initialize empty content
                            streamedTextRef.current = '';
                            let reasoningContent = '';
                            let hasFullResponse = false;

                            // Check if the result is iterable (a stream)
                            if (streamResult && typeof streamResult[Symbol.asyncIterator] === 'function') {
                                // Process each chunk from the stream
                                for await (const chunk of streamResult) {
                                    // Extract content from the chunk
                                    const content = chunk.choices[0]?.delta?.content || '';
                                    const reasoningChunk = chunk.choices[0]?.delta?.reasoning_content || '';

                                    if (content) {
                                        streamedTextRef.current += content;
                                    }

                                    if (reasoningChunk) {
                                        reasoningContent += reasoningChunk;
                                    }

                                    // When we get a completion in the response, mark as fully received
                                    if (chunk.choices[0]?.finish_reason) {
                                        hasFullResponse = true;
                                    }

                                    // Update UI with streamed content
                                    setConversations(prev =>
                                        prev.map(conv => {
                                            if (conv.id === currentConversationId) {
                                                const updatedMessages = conv.messages.map(msg => {
                                                    if (msg.id === assistantMessage.id) {
                                                        return {
                                                            ...msg,
                                                            content: streamedTextRef.current,
                                                            thinking: hasReasoning ? reasoningContent : undefined,
                                                            thinkingEndTime: hasFullResponse && hasReasoning ? Date.now() : msg.thinkingEndTime,
                                                            isThinking: hasReasoning && !hasFullResponse,
                                                            isSearching: false
                                                        };
                                                    }
                                                    return msg;
                                                });
                                                return { ...conv, messages: updatedMessages, updatedAt: Date.now() };
                                            }
                                            return conv;
                                        })
                                    );
                                }
                            } else {
                                // Fallback if streaming isn't supported - handle as a regular response
                                console.warn('Xai streaming not available, using non-streaming response');
                                const nonStreamResponse = streamResult as any;

                                streamedTextRef.current = nonStreamResponse.content || '';
                                reasoningContent = nonStreamResponse.reasoningContent || '';

                                setConversations(prev =>
                                    prev.map(conv => {
                                        if (conv.id === currentConversationId) {
                                            const updatedMessages = conv.messages.map(msg => {
                                                if (msg.id === assistantMessage.id) {
                                                    return {
                                                        ...msg,
                                                        content: streamedTextRef.current,
                                                        thinking: reasoningContent,
                                                        thinkingEndTime: Date.now(),
                                                        isThinking: false,
                                                        isSearching: false
                                                    };
                                                }
                                                return msg;
                                            });
                                            return { ...conv, messages: updatedMessages, updatedAt: Date.now() };
                                        }
                                        return conv;
                                    })
                                );
                            }
                        } catch (error) {
                            console.error('Error processing Xai stream:', error);
                            streamedTextRef.current = 'Sorry, there was an error generating the response.';

                            setConversations(prev =>
                                prev.map(conv => {
                                    if (conv.id === currentConversationId) {
                                        const updatedMessages = conv.messages.map(msg => {
                                            if (msg.id === assistantMessage.id) {
                                                return {
                                                    ...msg,
                                                    content: streamedTextRef.current,
                                                    isSearching: false
                                                };
                                            }
                                            return msg;
                                        });
                                        return { ...conv, messages: updatedMessages, updatedAt: Date.now() };
                                    }
                                    return conv;
                                })
                            );
                        }
                    } else if (modelProvider === 'Gemini') {
                        const geminiMessages = apiMessages.map(msg => ({
                            role: msg.role,
                            content: typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content)
                        }));

                        const response = await sendGeminiMessage(
                            selectedModel,
                            geminiMessages
                        );

                        // Check if response has 'content' property before accessing
                        const responseContent = typeof response === 'object' && response !== null && 'content' in response
                            ? response.content as string
                            : '';
                        streamedTextRef.current = responseContent;

                        setConversations(prev =>
                            prev.map(conv => {
                                if (conv.id === currentConversationId) {
                                    const updatedMessages = conv.messages.map(msg => {
                                        if (msg.id === assistantMessage.id) {
                                            // Use the checked content here as well
                                            return {
                                                ...msg,
                                                content: responseContent,
                                                isSearching: false
                                            };
                                        }
                                        return msg;
                                    });
                                    return { ...conv, messages: updatedMessages, updatedAt: Date.now() };
                                }
                                return conv;
                            })
                        );
                    } else {
                        const response = await groq.chat.completions.create({
                            messages: apiMessages,
                            model: customModel ? (customModel.id.startsWith('groq:') ? customModel.id.replace('groq:', '') : selectedModel) : selectedModel,
                            temperature: customModel?.temperature || 0.7,
                            top_p: customModel?.topP || 1,
                            max_tokens: customModel?.maxTokens,
                            frequency_penalty: customModel?.frequencyPenalty,
                            presence_penalty: customModel?.presencePenalty,
                            stop: customModel?.stop,
                            stream: true,
                        });

                        for await (const chunk of response) {
                            const content = chunk.choices[0]?.delta?.content || '';

                            if (content) {
                                streamedTextRef.current += content;

                                const conversation = conversations.find(c => c.id === currentConversationId);
                                const currentModel = conversation?.model || activeModel;
                                const hasReasoning = modelHasCapability(currentModel, 'reasoning');

                                if (hasReasoning) {
                                    const { thinking, content: cleanContent } = extractThinking(streamedTextRef.current);

                                    const thinkingIsFinished = thinking && cleanContent.trim().length > 0;

                                    setConversations(prev =>
                                        prev.map(conv => {
                                            if (conv.id === currentConversationId) {
                                                const updatedMessages = conv.messages.map(msg => {
                                                    if (msg.id === assistantMessage.id) {
                                                        return {
                                                            ...msg,
                                                            content: cleanContent,
                                                            thinking: thinking || msg.thinking,
                                                            thinkingEndTime: thinkingIsFinished ? Date.now() : msg.thinkingEndTime,
                                                            isThinking: !thinkingIsFinished,
                                                            isSearching: false
                                                        };
                                                    }
                                                    return msg;
                                                });

                                                return {
                                                    ...conv,
                                                    messages: updatedMessages,
                                                    updatedAt: Date.now()
                                                };
                                            }
                                            return conv;
                                        })
                                    );
                                } else {
                                    setConversations(prev =>
                                        prev.map(conv => {
                                            if (conv.id === currentConversationId) {
                                                const updatedMessages = conv.messages.map(msg => {
                                                    if (msg.id === assistantMessage.id) {
                                                        return {
                                                            ...msg,
                                                            content: streamedTextRef.current
                                                        };
                                                    }
                                                    return msg;
                                                });

                                                return {
                                                    ...conv,
                                                    messages: updatedMessages,
                                                    updatedAt: Date.now()
                                                };
                                            }
                                            return conv;
                                        })
                                    );
                                }
                            }
                        }
                    }

                    setConversations(prev =>
                        prev.map(conv => {
                            if (conv.id === currentConversationId) {
                                const updatedMessages = conv.messages.map(msg => {
                                    if (msg.id === assistantMessage.id) {
                                        return {
                                            ...msg,
                                            isThinking: false,
                                            isSearching: false,
                                            thinkingEndTime: msg.thinkingEndTime || Date.now(),
                                            searchEndTime: msg.searchEndTime || Date.now()
                                        };
                                    }
                                    return msg;
                                });
                                return {
                                    ...conv,
                                    messages: updatedMessages,
                                    updatedAt: Date.now()
                                };
                            }
                            return conv;
                        })
                    );

                    setStreamingStatus('done');
                    showNotification('Response completed', 'success');
                } catch (apiError) {
                    console.error('API error:', apiError);
                    setConversations(prev =>
                        prev.map(conv => {
                            if (conv.id === currentConversationId) {
                                const updatedMessages = conv.messages.map(msg => {
                                    if (msg.id === assistantMessage.id) {
                                        return {
                                            ...msg,
                                            content: "Sorry, I encountered an error processing your request. Please try again later."
                                        };
                                    }
                                    return msg;
                                });

                                return {
                                    ...conv,
                                    messages: updatedMessages,
                                    updatedAt: Date.now()
                                };
                            }
                            return conv;
                        })
                    );

                    setStreamingStatus('error');
                    showNotification('Error processing your request', 'error');
                }

                console.log('Attempting to increment usage...');
                try {
                    await incrementUsage(currentUser.uid, modelId);
                    const remaining = await getRemainingMessages(currentUser.uid);
                    setRemainingMessages(remaining);
                    console.log('Usage incremented successfully.');
                } catch (error) {
                    console.error('Error incrementing usage:', error);
                }
            } catch (error) {
                console.error('Error in handleSendMessage:', error);
                setStreamingStatus('error');
                showNotification('An unexpected error occurred', 'error');
            }
        } catch (error) {
            console.error('Error sending message:', error);
            setStreamingStatus('error');
            showNotification('Failed to send message', 'error');
        }
    };

    useEffect(() => {
        // Check if current model is in maintenance mode
        const currentModel = getModelById(activeModel);
        if (currentModel?.maintenance) {
            // Find the first available model that's not in maintenance
            const availableModel = models.find(m => !m.maintenance);
            if (availableModel) {
                setActiveModel(availableModel.id);
                showNotification(`Model ${currentModel.name} is currently under maintenance. Switched to ${availableModel.name}.`, 'info');
            }
        }
    }, []); // Only run once on initial load

    const handleModelChange = (modelId: string) => {
        const model = getModelById(modelId);

        // Don't allow switching to a model in maintenance mode
        if (model?.maintenance) {
            showNotification(`${model.name} is currently under maintenance and unavailable.`, 'error');
            return;
        }

        setActiveModel(modelId);

        if (currentConversationId) {
            setConversations(prev =>
                prev.map(conv => {
                    if (conv.id === currentConversationId) {
                        return {
                            ...conv,
                            model: modelId
                        };
                    }
                    return conv;
                })
            );

            const modelName = allModels.find(m => m.id === modelId)?.name || modelId;
            showNotification(`Switched to ${modelName}`, 'info');
        }
    };

    // Handle editing previous user prompts
    const handleEditRequest = (messageId: string) => {
        if (!currentConversation) return;

        // Find the user message by its ID (not the assistant message anymore)
        const userMessage = currentConversation.messages.find(
            message => message.id === messageId && message.role === 'user'
        );

        if (userMessage) {
            // Set the message content for editing
            const userContent = typeof userMessage.content === 'string'
                ? userMessage.content
                : userMessage.content
                    .filter(item => item.type === 'text')
                    .map(item => item.type === 'text' ? item.text || '' : '')
                    .join('\n');

            setEditingMessageId(userMessage.id);
            setEditingMessageContent(userContent);

            // Focus the textarea
            setTimeout(() => {
                const textarea = document.querySelector('textarea');
                if (textarea) {
                    textarea.focus();
                    textarea.value = userContent;
                    const event = new Event('input', { bubbles: true });
                    textarea.dispatchEvent(event);
                }
            }, 100);

            // Find the index of the message in the conversation
            const messageIndex = currentConversation.messages.findIndex(
                message => message.id === messageId
            );

            // Truncate the conversation to remove this message and all subsequent messages
            if (messageIndex >= 0) {
                setConversations(prev =>
                    prev.map(conv => {
                        if (conv.id === currentConversationId) {
                            return {
                                ...conv,
                                messages: conv.messages.slice(0, messageIndex)
                            };
                        }
                        return conv;
                    })
                );
            }
        }
    };

    // Cancel editing
    const handleCancelEdit = () => {
        setEditingMessageId(null);
        setEditingMessageContent('');
    };

    // Handle message regeneration
    const handleRegenerateRequest = (messageId: string) => {
        if (!currentConversation) return;

        // Find the assistant message that needs to be regenerated
        const messageIndex = currentConversation.messages.findIndex(
            message => message.id === messageId && message.role === 'assistant'
        );

        if (messageIndex > 0) {
            // Set regeneration mode instead of immediately regenerating
            setRegenerationMessageId(messageId);
            setRegenerateModel(currentConversation.model || activeModel);
            setIsRegenerating(true);
        }
    };

    // Execute regeneration with the selected model
    const executeRegeneration = (modelId: string) => {
        if (!currentConversation || !regenerationMessageId) return;

        // Find the assistant message that needs to be regenerated
        const messageIndex = currentConversation.messages.findIndex(
            message => message.id === regenerationMessageId && message.role === 'assistant'
        );

        if (messageIndex > 0) {
            // Get the previous user message
            const userMessage = currentConversation.messages[messageIndex - 1];

            // Truncate the conversation to remove the assistant message and all subsequent messages
            setConversations(prev =>
                prev.map(conv => {
                    if (conv.id === currentConversationId) {
                        return {
                            ...conv,
                            messages: conv.messages.slice(0, messageIndex),
                            model: modelId // Update the conversation model
                        };
                    }
                    return conv;
                })
            );

            // Reset regeneration state
            setIsRegenerating(false);
            setRegenerationMessageId(null);

            // Update active model
            setActiveModel(modelId);

            // Resend the user message
            if (userMessage.role === 'user') {
                const content = typeof userMessage.content === 'string'
                    ? userMessage.content
                    : userMessage.content
                        .filter(item => item.type === 'text')
                        .map(item => item.type === 'text' ? item.text || '' : '')
                        .join('\n');

                // Get any files from the original message
                const files = userMessage.content && typeof userMessage.content !== 'string'
                    ? userMessage.content
                        .filter(item => item.type === 'file_content' || item.type === 'image_url')
                        .map(item => {
                            if (item.type === 'file_content') {
                                return {
                                    id: item.file_id || uuidv4(),
                                    name: item.name || 'file',
                                    type: 'text/plain',
                                    size: (item.content as string).length,
                                    content: item.content,
                                    isText: true
                                } as UploadedFile;
                            }
                            return null;
                        })
                        .filter(Boolean) as UploadedFile[]
                    : undefined;

                // Using a timeout to ensure the UI updates first
                setTimeout(() => {
                    handleSendMessage(content, files, searchEnabled);
                }, 100);
            }
        }
    };

    // Cancel regeneration
    const cancelRegeneration = () => {
        setIsRegenerating(false);
        setRegenerationMessageId(null);
    };

    const handleCopyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text)
            .then(() => {
                showNotification('Copied to clipboard', 'success');
            })
            .catch(() => {
                showNotification('Failed to copy to clipboard', 'error');
            });
    };

    return (
        <div className="h-screen flex flex-col overflow-hidden bg-background-primary text-text-primary dark:bg-dark-background-primary dark:text-dark-text-primary">
            <BackgroundEffects />

            <div
                className="fixed inset-0 pointer-events-none opacity-[0.025] dark:opacity-[0.025] z-0"
                style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
                }}
            />

            <div className="flex flex-1 h-full overflow-hidden">
                <div
                    className={`
                        w-80 h-full transition-all duration-300 ease-in-out overflow-y-auto flex flex-col
                        bg-background-secondary dark:bg-dark-background-secondary border-r border-glass-border dark:border-dark-glass-border rounded-tr-2xl
                        ${showSidebar ? 'translate-x-0' : '-translate-x-full'}
                    `}
                >
                    <div className="p-4 flex justify-between items-center border-b border-glass-border dark:border-dark-glass-border">
                        <h1 className="text-xl font-bold text-text-primary dark:text-dark-text-primary">Versetaille</h1>
                    </div>

                    <div className="flex-1 overflow-y-auto px-4">
                        <ConversationList
                            conversations={conversations}
                            currentConversationId={currentConversationId}
                            onSelect={setCurrentConversationId}
                            onDelete={handleDeleteConversation}
                            onNewConversation={handleNewConversation}
                        />
                    </div>

                    <div className="p-4 border-t border-glass-border dark:border-dark-glass-border mt-auto">
                        <ProfileButton onClick={() => setIsSettingsOpen(true)} />
                    </div>
                </div>

                <div className="flex-1 flex flex-col h-full relative overflow-hidden">
                    <header className="flex items-center justify-between px-4 py-3 bg-background-secondary/80 dark:bg-dark-background-secondary/80 border-b border-glass-border dark:border-dark-glass-border z-20 rounded-bl-2xl backdrop-blur-sm">
                        <button
                            onClick={() => setShowSidebar(!showSidebar)}
                            className="text-text-primary dark:text-dark-text-primary hover:bg-background-tertiary/50 dark:hover:bg-dark-background-tertiary/50 p-2 rounded-lg"
                            aria-label="Toggle Sidebar"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                            </svg>
                        </button>

                        <div className="flex items-center">
                            <img src="/logos/versetaille.svg" alt="Versetaille Logo" className="h-6 w-6 mr-2" />
                            <h1 className="text-lg font-semibold">Versetaille</h1>
                        </div>

                        <div className="flex items-center space-x-4">
                            <ModelSelector
                                models={allModels}
                                selectedModel={activeModel}
                                onSelect={handleModelChange}
                                userPlan={userPlan}
                                onPlanChange={refreshUserData}
                            />
                            <button
                                onClick={() => setSearchEnabled(!searchEnabled)}
                                className={`p-2 rounded-lg ${searchEnabled ? 'bg-primary-500/20 text-primary-500' : 'hover:bg-background-tertiary/50 dark:hover:bg-dark-background-tertiary/50'}`}
                                aria-label="Toggle Search"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                                </svg>
                            </button>
                        </div>
                    </header>

                    <div className="flex-1 overflow-auto">
                        <div className="h-full flex flex-col">
                            <div className="flex-1 p-4 space-y-6">
                                {currentConversation?.messages.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center h-full text-center">
                                        <div className="text-4xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-8">
                                            Versetaille
                                        </div>
                                        <div className="text-xl text-text-secondary dark:text-dark-text-secondary mb-6">
                                            {username ? `Hello, ${username}! How can I assist you today?` : 'Your AI conversation assistant'}
                                        </div>
                                        {(isPlanLoading || isRateLimitChecking) ? (
                                            <div className="bg-background-tertiary/50 dark:bg-dark-background-tertiary/50 rounded-2xl p-4 mb-6 max-w-2xl flex items-center justify-center text-text-secondary dark:text-dark-text-secondary">
                                                <div className="w-5 h-5 border-2 border-gray-400 dark:border-gray-500 border-t-transparent rounded-full animate-spin mr-3"></div>
                                                <span>Loading user data...</span>
                                            </div>
                                        ) : remainingMessages === 0 && userPlan === 'free' ? (
                                            <div className="bg-red-500/10 dark:bg-red-500/20 text-red-600 dark:text-red-400 rounded-2xl p-4 mb-6 max-w-2xl">
                                                <h3 className="text-lg font-medium mb-2">Daily Limit Reached</h3>
                                                <p>You've used all {getMessageLimit(userPlan)} messages for today. Limit resets at {rateLimitReset.formattedTime}.</p>
                                            </div>
                                        ) : (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
                                                <div className="bg-background-tertiary dark:bg-dark-background-tertiary rounded-2xl p-4 hover:shadow-glow dark:hover:shadow-dark-glow transition-all border border-glass-border dark:border-dark-glass-border cursor-pointer"
                                                    onClick={() => handleSendMessage("Tell me about yourself. What are your capabilities?")}>
                                                    <div className="font-medium mb-2 text-text-primary dark:text-dark-text-primary">What can you do?</div>
                                                    <div className="text-sm text-text-secondary dark:text-dark-text-secondary">Learn about my capabilities</div>
                                                </div>
                                                <div className="bg-background-tertiary dark:bg-dark-background-tertiary rounded-2xl p-4 hover:shadow-glow dark:hover:shadow-dark-glow transition-all border border-glass-border dark:border-dark-glass-border cursor-pointer"
                                                    onClick={() => handleSendMessage("Write a poem about the stars and universe in a beautiful style")}>
                                                    <div className="font-medium mb-2 text-text-primary dark:text-dark-text-primary">Generate creative content</div>
                                                    <div className="text-sm text-text-secondary dark:text-dark-text-secondary">Poems, stories, ideas and more</div>
                                                </div>
                                                <div className="bg-background-tertiary dark:bg-dark-background-tertiary rounded-2xl p-4 hover:shadow-glow dark:hover:shadow-dark-glow transition-all border border-glass-border dark:border-dark-glass-border cursor-pointer"
                                                    onClick={() => handleSendMessage("Explain quantum computing in simple terms")}>
                                                    <div className="font-medium mb-2 text-text-primary dark:text-dark-text-primary">Explain complex topics</div>
                                                    <div className="text-sm text-text-secondary dark:text-dark-text-secondary">Get explanations on any subject</div>
                                                </div>
                                                <div className="bg-background-tertiary dark:bg-dark-background-tertiary rounded-2xl p-4 hover:shadow-glow dark:hover:shadow-dark-glow transition-all border border-glass-border dark:border-dark-glass-border cursor-pointer"
                                                    onClick={() => handleSendMessage("How do I improve my productivity? Give me some practical tips.")}>
                                                    <div className="font-medium mb-2 text-text-primary dark:text-dark-text-primary">Get practical advice</div>
                                                    <div className="text-sm text-text-secondary dark:text-dark-text-secondary">Tips and solutions for everyday challenges</div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        {currentConversation?.messages.map((message, index) => (
                                            <ChatMessage
                                                key={message.id}
                                                message={message}
                                                isStreaming={isStreaming(streamingStatus) && index === currentConversation.messages.length - 1}
                                                modelId={currentConversation.model || activeModel}
                                                onEditRequest={handleEditRequest}
                                                onRegenerateRequest={handleRegenerateRequest}
                                                onCopyResponse={(content) => {
                                                    const text = typeof content === 'string'
                                                        ? content
                                                        : content
                                                            .filter(item => item.type === 'text')
                                                            .map(item => item.type === 'text' ? item.text || '' : '')
                                                            .join('\n');

                                                    handleCopyToClipboard(text);
                                                }}
                                            />
                                        ))}
                                    </>
                                )}
                                <div ref={messagesEndRef} />
                            </div>
                        </div>
                    </div>

                    <div className="p-4 backdrop-blur-sm bg-background-secondary/70 border-t border-glass-border">
                        <ChatInput
                            onSubmit={handleSendMessage}
                            onStopGeneration={() => {
                                // Handle stopping generation
                                setStreamingStatus('idle');
                            }}
                            disabled={streamingStatus === 'streaming' || isRateLimitChecking}
                            isStreaming={streamingStatus === 'streaming'}
                            placeholder={
                                editingMessageId
                                    ? 'Edit your message...'
                                    : streamingStatus === 'streaming'
                                        ? 'Versetaille is thinking...'
                                        : 'Type your message here...'
                            }
                            initialValue={editingMessageContent}
                            isEditing={editingMessageId !== null}
                            onCancelEdit={handleCancelEdit}
                            searchEnabled={searchEnabled}
                            onSearchToggle={() => setSearchEnabled(!searchEnabled)}
                            errorState={streamingStatus === 'error'}
                            onCopyText={handleCopyToClipboard}
                        />
                    </div>
                </div>
            </div>

            <Settings
                isOpen={isSettingsOpen}
                onClose={() => setIsSettingsOpen(false)}
                username={username}
                onUsernameChange={setUsername}
                modelBehavior={modelBehavior}
                onModelBehaviorChange={setModelBehavior}
                userPlan={userPlan}
                onPlanChange={refreshUserData}
                currentTheme={theme}
                onToggleTheme={toggleTheme}
            />

            {/* Model Selection Dialog for Regeneration */}
            {isRegenerating && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
                        <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
                            Regenerate Response
                        </h3>
                        <p className="mb-4 text-gray-700 dark:text-gray-300">
                            Choose which model to use for regenerating the response:
                        </p>

                        <div className="mb-6 space-y-2 max-h-60 overflow-y-auto">
                            {allModels.map(model => (
                                <div
                                    key={model.id}
                                    className={`p-3 rounded-md cursor-pointer flex items-center gap-3 ${regenerateModel === model.id
                                        ? 'bg-blue-100 dark:bg-blue-900/30 border border-blue-500'
                                        : 'hover:bg-gray-100 dark:hover:bg-gray-700 border border-transparent'
                                        }`}
                                    onClick={() => setRegenerateModel(model.id)}
                                >
                                    <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                                        {model.icon ?
                                            model.icon :
                                            <div className="w-6 h-6 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                                        }
                                    </div>
                                    <div className="flex-grow">
                                        <div className="font-medium text-gray-900 dark:text-gray-100">{model.name || model.id}</div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400">
                                            {typeof model.description === 'string' ? model.description : ''}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={cancelRegeneration}
                                className="px-4 py-2 rounded-md text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => regenerateModel && executeRegeneration(regenerateModel)}
                                className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={!regenerateModel}
                            >
                                Regenerate
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function App() {
    return (
        <AuthProvider>
            <NotificationProvider>
                <ThemeProvider>
                    <AppContent />
                </ThemeProvider>
            </NotificationProvider>
        </AuthProvider>
    );
}

function AppContent() {
    const { currentUser, loading } = useAuth();

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-background-primary text-text-primary dark:bg-dark-background-primary dark:text-dark-text-primary">
                <div className="loading loading-spinner loading-lg"></div>
            </div>
        );
    }

    return currentUser ? <MainApp /> : <AuthScreen />;
}

export default App;